//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { useState } from "react";

// Material ui Imports
import { SxProps } from "@mui/system";
import { Grid, Typography, styled, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchRounded";

// Custom components imports (and hooks and helpers)
import { VTButton, VTTextField } from "@virtus-tech-repository/virtus-tech-repository";

// Media imports
import scene3D from "../../assets/images/defaultTemplates/3D-scene.png";
import scene360 from "../../assets/images/defaultTemplates/360-scene.png";
import sceneDocument from "../../assets/images/defaultTemplates/document-scene.png";
import scenePassthrough from "../../assets/images/defaultTemplates/Passthrough.png";
// import scenePassthrough from "../../assets/images/defaultTemplates/passthrough-scene.png";
import VTImageDraggable from "./VTImageDraggable";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { EDraggableOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { IDragOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/current.model";
import { setCurrentDoubleClick } from "../../store/slices/current.slice";

export const TemplateSceneImg = styled("img")(({ theme }) => ({
    borderRadius: "15px",
    height: "200px",
    width: "100%",
    objectFit: "cover",
    ":hover": {
        cursor: "pointer",
    },
}));

//---------------------------------------------------------------------------------------------------
// ## INTERFACES ##
interface IProps {
    sx?: SxProps;
    handleDragStart?: (mediaType: EDraggableOptions, src: string, preview?: string) => void;
    handleDragStop?: () => void;
    videoCurrentState: "Paused" | "Playing" | "Question" | undefined;
}

export default function VTTemplateSidePanel({ handleDragStart, handleDragStop, sx, videoCurrentState }: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##

    const dispatch = useAppDispatch();
    const theme = useTheme();

    const [activeButton, setActiveButton] = useState(0);
    const [searchValue, setSearchValue] = useState<string>("");
    const [dragging, setDragging] = useState<boolean>(false);
    const { organisation } = useAppSelector((state) => state.userReducer);
    const { currentQuestion } = useAppSelector((state) => state.currentReducer);

    //-------------------------------------------------------------------------------------------------
    // ## CUSTOM FUNCTIONS ##
    function handleButtonClick(buttonIndex: number) {
        setActiveButton(buttonIndex);
    }

    function localHandleDrag(
        dragged: boolean,
        mediaType?: EDraggableOptions,
        src?: string,
        preview?: string | undefined,
    ) {
        if (dragged && handleDragStart && mediaType && src) {
            handleDragStart(mediaType, src, preview);
        } else if (!dragged && handleDragStop) {
            handleDragStop();
        }
    }

    function handleDoubleClick(mediaType: EDraggableOptions, src: string) {
        dispatch(setCurrentDoubleClick({ src: src, mediaType: mediaType }));
    }

    return (
        <Grid
            container
            justifyContent="center"
            alignItems="flex-start"
            direction="column"
            sx={{ height: "calc(100vh - 70px)", padding: "10px", ...sx }}
        >
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Images search */}
            <Grid
                item
                xs={1}
                sx={{
                    padding: "10px",
                    width: "100%",
                    paddingTop: "40px",
                }}
            >
                <VTTextField
                    placeholder={"Search Templates"}
                    sx={{
                        height: "60px",
                        borderRadius: "10px",
                        width: "100%",
                    }}
                    value={searchValue}
                    onChange={(textInput: string) => setSearchValue(textInput)}
                    type={"text"}
                    endIcon={<SearchIcon />}
                />
            </Grid>

            {/*-----------------------------------------------------------------------------------------*/}
            {/* Filters */}
            <Grid item container xs={1} sx={{ height: "120px", marginTop: "5px" }}>
                <VTButton
                    onClick={() => handleButtonClick(0)}
                    sx={{
                        borderBottom: activeButton === 0 ? `3px solid ${theme.palette.primary.main}` : "none",
                        maxWidth: "100%",
                        height: "60px",
                    }}
                    type="underline"
                >
                    <Typography>Template Views</Typography>
                </VTButton>

                <VTButton
                    onClick={() => handleButtonClick(1)}
                    sx={{
                        borderBottom: activeButton === 1 ? `3px solid ${theme.palette.primary.main}` : "none",
                        maxWidth: "100%",
                    }}
                    type="underline"
                >
                    <Typography> Scenarios</Typography>
                </VTButton>
            </Grid>

            <Grid
                item
                container
                spacing={2}
                sx={{
                    display: "flex",
                    flex: "1",
                    overflowY: "auto",
                }}
            >
                <Grid item xs={12}>
                    <VTImageDraggable
                        item={{
                            name: "Blank Scene",
                            src: "https://medias3.digi-sim-api.co.uk/app_defaults/default_360_image",
                        }}
                        organisation={organisation}
                        localHandleDrag={localHandleDrag}
                        handleDoubleClick={handleDoubleClick}
                        xs={12}
                        type={EDraggableOptions.IMMERSIVE_IMAGE}
                        displaySrc={scene360}
                    />
                </Grid>
                <Grid item xs={12}>
                    <VTImageDraggable
                        item={{
                            name: "Document",
                        }}
                        organisation={organisation}
                        localHandleDrag={localHandleDrag}
                        handleDoubleClick={handleDoubleClick}
                        xs={12}
                        type={EDraggableOptions.DOCUMENT}
                        displaySrc={sceneDocument}
                    />
                </Grid>
                <Grid item xs={12}>
                    <VTImageDraggable
                        item={{
                            name: "Passthrough Scene",
                        }}
                        organisation={organisation}
                        localHandleDrag={localHandleDrag}
                        handleDoubleClick={handleDoubleClick}
                        xs={12}
                        type={EDraggableOptions.PASSTHROUGH}
                        displaySrc={scenePassthrough}
                    />
                </Grid>
                <Grid item xs={12}>
                    <VTImageDraggable
                        item={{
                            name: "Models Scene",
                        }}
                        organisation={organisation}
                        localHandleDrag={localHandleDrag}
                        handleDoubleClick={handleDoubleClick}
                        xs={12}
                        type={EDraggableOptions.MODEL_3D}
                        displaySrc={scene3D}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}
